import Decimal from "decimal.js";
import type {
  AddDedSummary,
  AddDedRow,
  AddDedSplit,
  CommodityDeductionAdditionLookup,
} from "@/lib/api/agtech/ticketsExpanded";
import { fmt, fmtNum } from "./TicketComponents";

// ── Helpers ────────────────────────────────────────────────────────

function fmtDollar(val: string | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return `$${new Decimal(val).toNumber().toLocaleString()}`;
}

function fmtPct(val: string | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return `${new Decimal(val).times(100).toDP(2).toNumber()}%`;
}

function customerLabel(
  ref: { customer_id: string; long_name: string } | null,
  fallbackId: string | null,
): string {
  if (ref) return ref.long_name;
  if (fallbackId) return fallbackId;
  return "—";
}

// ── Commodity rate inline ──────────────────────────────────────────

function CommodityRateChips({ rates }: { rates: CommodityDeductionAdditionLookup[] }) {
  if (rates.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
      {rates.map((r) => (
        <div key={r.entity_id} className="text-[10px] text-muted-foreground">
          <span className="font-medium">Tbl {r.da_table_nbr}:</span>
          {r.rate !== null && <span className="ml-1">rate {fmt(r.rate)}</span>}
          {r.rate_mode && <span className="ml-1">· mode {r.rate_mode}</span>}
          {r.weight_basis && <span className="ml-1">· basis {r.weight_basis}</span>}
        </div>
      ))}
    </div>
  );
}

// ── Lines table ────────────────────────────────────────────────────

function LinesTable({ rows }: { rows: AddDedRow[] }) {
  if (rows.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-[10px] font-medium text-muted-foreground">
            <th className="text-left py-1 pr-3">Add/Ded</th>
            <th className="text-left py-1 pr-3">Location</th>
            <th className="text-right py-1">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/50 align-top">
              <td className="py-1 pr-3">
                <div>{r.description ?? "—"}</div>
                <CommodityRateChips rates={r.commodityRates} />
              </td>
              <td className="py-1 pr-3">{fmt(r.Location?.long_name ?? r.plc_id)}</td>
              <td className="text-right py-1">{fmtDollar(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Splits table ───────────────────────────────────────────────────

function SplitsTable({
  splits,
  netSettlementTotal,
  totalFees,
  isAdvance,
}: {
  splits: AddDedSplit[];
  netSettlementTotal: Decimal;
  totalFees: Decimal;
  isAdvance: boolean;
}) {
  if (splits.length === 0) return null;

  const totalAmount = splits.reduce(
    (acc: Decimal, s: AddDedSplit) =>
      s.settlement_amount !== null ? acc.plus(new Decimal(s.settlement_amount)) : acc,
    new Decimal(0),
  );
  const totalPaymentAmount = splits.reduce(
    (acc: Decimal, s: AddDedSplit) =>
      s.payment_amount !== null ? acc.plus(new Decimal(s.payment_amount)) : acc,
    new Decimal(0),
  );
  const paymentAmount = netSettlementTotal.minus(totalFees);

  return (
    <div className="overflow-x-auto">
      <span className="text-[10px] font-medium text-muted-foreground">Splits</span>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-[10px] font-medium text-muted-foreground">
            <th className="text-left py-1 pr-3">#</th>
            <th className="text-left py-1 pr-3">Payee</th>
            <th className="text-left py-1 pr-3">Vendor</th>
            <th className="text-right py-1 pr-3">Split %</th>
            <th className="text-right py-1 pr-3">Amount</th>
            {!isAdvance && <th className="text-right py-1 pr-3">Less Other</th>}
            {isAdvance && <th className="text-right py-1 pr-3">Adv %</th>}
            <th className="text-left py-1 pr-3">Pmt Method</th>
            <th className="text-right py-1">Pmt Amount</th>
          </tr>
        </thead>
        <tbody>
          {splits.map((s, i) => (
            <tr key={i} className="border-b border-border/50">
              <td className="py-1 pr-3">{s.sort_order}</td>
              <td className="py-1 pr-3">{customerLabel(s.PayeeCustomer, s.payee_id)}</td>
              <td className="py-1 pr-3">{customerLabel(s.VendorCustomer, s.vendor_customer_id)}</td>
              <td className="text-right py-1 pr-3">{fmtPct(s.split_percent)}</td>
              <td className="text-right py-1 pr-3">{fmtDollar(s.settlement_amount)}</td>
              {!isAdvance && (
                <td className="text-right py-1 pr-3">{fmtNum(s.less_other_amount)}</td>
              )}
              {isAdvance && (
                <td className="text-right py-1 pr-3">{fmtPct(s.advance_percent)}</td>
              )}
              <td className="py-1 pr-3">{fmt(s.PaymentMethod?.description ?? s.payment_method)}</td>
              <td className="text-right py-1">{fmtDollar(s.payment_amount)}</td>
            </tr>
          ))}
          {isAdvance ? (
            <tr className="border-t border-border">
              <td className="text-right py-1.5 pr-3 font-semibold" colSpan={8}>
                Total
              </td>
              <td className="text-right py-1.5 font-semibold">
                {fmtDollar(totalPaymentAmount.toString())}
              </td>
            </tr>
          ) : (
            <tr className="border-t border-border">
              <td className="py-1.5 pr-3" />
              <td className="py-1.5 pr-3" />
              <td className="py-1.5 pr-3" />
              <td className="text-right py-1.5 pr-3 font-semibold">Total</td>
              <td className="text-right py-1.5 pr-3 font-semibold">
                {fmtDollar(totalAmount.toString())}
              </td>
              <td className="py-1.5 pr-3" colSpan={2}>
                <NetSettlementFormula
                  netSettlement={netSettlementTotal}
                  totalFees={totalFees}
                />
              </td>
              <td className="text-right py-1.5 font-semibold">
                {fmtDollar(paymentAmount.toString())}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Net Settlement formula (mirrors CashFormulaLine style) ────────

function NetSettlementFormula({
  netSettlement,
  totalFees,
}: {
  netSettlement: Decimal;
  totalFees: Decimal;
}) {
  const netFmt = netSettlement.toNumber().toLocaleString();
  const feesFmt = totalFees.toNumber().toLocaleString();

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">
        Net Settlement − Total Fees
      </span>
      <div className="flex items-center gap-1 text-xs flex-wrap">
        <span className="text-emerald-600 dark:text-emerald-400 font-medium">
          ${netFmt}
        </span>
        <span className="text-muted-foreground">−</span>
        <span className="text-red-500 dark:text-red-400 font-medium">
          ${feesFmt}
        </span>
        <span className="text-muted-foreground">=</span>
      </div>
    </div>
  );
}

// ── Summary block (per settlement/advance) ────────────────────────

function SummaryBlock({
  summary,
  netSettlementTotal,
}: {
  summary: AddDedSummary;
  netSettlementTotal: Decimal;
}) {
  const hasRows = summary.rows.length > 0;
  const hasSplits = summary.splits.length > 0;
  if (!hasRows && !hasSplits) return null;

  const label = summary.entity_type === "settlement" ? "Settlement" : "Advance";

  // Total fees = sum of add/ded row amounts (NOT splits).
  const totalFees = summary.rows.reduce(
    (acc: Decimal, r: AddDedRow) =>
      r.amount !== null ? acc.plus(new Decimal(r.amount)) : acc,
    new Decimal(0),
  );

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>
      <LinesTable rows={summary.rows} />
      {hasSplits && (
        <SplitsTable
          splits={summary.splits}
          netSettlementTotal={netSettlementTotal}
          totalFees={totalFees}
          isAdvance={summary.entity_type === "advance"}
        />
      )}
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────

export function AddDedSummaryCard({
  summaries,
  netSettlementTotal,
}: {
  summaries: AddDedSummary[];
  netSettlementTotal: Decimal;
}) {
  const populated = summaries.filter(
    (s) => s.rows.length > 0 || s.splits.length > 0,
  );
  if (populated.length === 0) return null;

  return (
    <div className="rounded-lg border bg-muted/50 p-3 space-y-3">
      <span className="text-xs font-semibold">Addition / Deduction Summary</span>
      {populated.map((summary) => (
        <SummaryBlock
          key={`${summary.entity_type}-${summary.entity_id_nbr}`}
          summary={summary}
          netSettlementTotal={netSettlementTotal}
        />
      ))}
    </div>
  );
}
